/**
 * Port of `repos/opencode/packages/ui/src/components/diff-changes.tsx`.
 *
 * The block allocation is upstream's, transcribed rather than rewritten: it caps bars by
 * change magnitude and reserves a neutral block at low totals, so a one-line edit does not
 * read as a whole-file rewrite. Behaviour lives in `allocateBlocks`, which is exported so
 * the rules can be tested without rendering.
 */

import type { ReactNode } from "react";

const TOTAL_BLOCKS = 5;

const ADD_COLOR = "var(--icon-diff-add-base)";
const DELETE_COLOR = "var(--icon-diff-delete-base)";
const NEUTRAL_COLOR = "var(--icon-weak-base)";

export interface Changes {
  additions: number;
  deletions: number;
}

export interface DiffChangesProps {
  className?: string;
  changes: Changes | Changes[];
  variant?: "default" | "bars";
}

export function allocateBlocks(adds: number, dels: number) {
  if (adds === 0 && dels === 0) return { added: 0, deleted: 0, neutral: TOTAL_BLOCKS };

  const total = adds + dels;

  if (total < 5) {
    const added = adds > 0 ? 1 : 0;
    const deleted = dels > 0 ? 1 : 0;
    return { added, deleted, neutral: TOTAL_BLOCKS - added - deleted };
  }

  const ratio = adds > dels ? adds / dels : dels / adds;
  let blocksForColors = TOTAL_BLOCKS;
  if (total < 20) blocksForColors = TOTAL_BLOCKS - 1;
  else if (ratio < 4) blocksForColors = TOTAL_BLOCKS - 1;

  const addedRaw = (adds / total) * blocksForColors;
  const deletedRaw = (dels / total) * blocksForColors;

  let added = adds > 0 ? Math.max(1, Math.round(addedRaw)) : 0;
  let deleted = dels > 0 ? Math.max(1, Math.round(deletedRaw)) : 0;

  // Cap bars based on actual change magnitude.
  if (adds > 0 && adds <= 5) added = Math.min(added, 1);
  if (adds > 5 && adds <= 10) added = Math.min(added, 2);
  if (dels > 0 && dels <= 5) deleted = Math.min(deleted, 1);
  if (dels > 5 && dels <= 10) deleted = Math.min(deleted, 2);

  if (added + deleted > blocksForColors) {
    if (addedRaw > deletedRaw) added = blocksForColors - deleted;
    else deleted = blocksForColors - added;
  }

  return { added, deleted, neutral: Math.max(0, TOTAL_BLOCKS - added - deleted) };
}

function sum(changes: Changes | Changes[], key: keyof Changes) {
  return Array.isArray(changes)
    ? changes.reduce((acc, diff) => acc + (diff[key] ?? 0), 0)
    : changes[key];
}

export function DiffChanges({
  className,
  changes,
  variant = "default"
}: DiffChangesProps): ReactNode {
  const additions = sum(changes, "additions");
  const deletions = sum(changes, "deletions");

  if (variant === "default" && additions + deletions <= 0) return null;

  const counts = allocateBlocks(additions ?? 0, deletions ?? 0);
  const blocks = [
    ...Array<string>(counts.added).fill(ADD_COLOR),
    ...Array<string>(counts.deleted).fill(DELETE_COLOR),
    ...Array<string>(counts.neutral).fill(NEUTRAL_COLOR)
  ].slice(0, TOTAL_BLOCKS);

  return (
    <div data-component="diff-changes" data-variant={variant} className={className}>
      {variant === "bars" ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 14" fill="none">
          <g>
            {blocks.map((color, index) => (
              <rect key={index} x={index * 4} width="2" height="14" rx="1" fill={color} />
            ))}
          </g>
        </svg>
      ) : (
        <>
          <span data-slot="diff-changes-additions">{`+${additions}`}</span>
          <span data-slot="diff-changes-deletions">{`-${deletions}`}</span>
        </>
      )}
    </div>
  );
}
